import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { tenantsApi, backupsApi } from '@/lib/api';
import { Cloud, ArrowClockwise, Eye, EyeSlash } from '@phosphor-icons/react';

export const R2SettingsForm = ({ tenantConfig, setTenantConfig }) => {
  // Backups State
  const [backupsList, setBackupsList] = useState([]);
  const [backupsConfigured, setBackupsConfigured] = useState(false);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [backupsSubTab, setBackupsSubTab] = useState('history');
  const [r2AccountId, setR2AccountId] = useState('');
  const [r2AccessKeyId, setR2AccessKeyId] = useState('');
  const [r2SecretAccessKey, setR2SecretAccessKey] = useState('');
  const [r2BucketName, setR2BucketName] = useState('');
  const [r2BackupFrequency, setR2BackupFrequency] = useState(24);
  const [showR2Secret, setShowR2Secret] = useState(false);
  const [testingR2, setTestingR2] = useState(false);
  const [savingR2, setSavingR2] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [resettingDb, setResettingDb] = useState(false);

  useEffect(() => {
    if (tenantConfig) {
      if (tenantConfig.r2_config) {
        setR2AccountId(tenantConfig.r2_config.account_id || '');
        setR2AccessKeyId(tenantConfig.r2_config.access_key_id || '');
        // The API never echoes the stored secret back (see backend _mask_secret_config) —
        // "secret_access_key": null means one's already set; leave blank either way, and
        // submitting blank means "keep the existing key."
        setR2SecretAccessKey('');
        setR2BucketName(tenantConfig.r2_config.bucket_name || '');
        setR2BackupFrequency(tenantConfig.r2_config.backup_frequency_hours || 24);
      } else {
        setR2AccountId('');
        setR2AccessKeyId('');
        setR2SecretAccessKey('');
        setR2BucketName('');
        setR2BackupFrequency(24);
      }
    }
  }, [tenantConfig]);

  useEffect(() => {
    fetchBackups();
  }, []);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const r2SecretConfigured = tenantConfig?.r2_config?.secret_access_key === null;

  const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalCapacityBytes = backupsList
    .filter(b => b.status === 'success')
    .reduce((acc, curr) => acc + (curr.db_file_size || 0) + (curr.uploads_file_size || 0), 0);

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const { data } = await backupsApi.list();
      setBackupsConfigured(data.is_configured);
      setBackupsList(data.backups || []);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
      toast.error('Failed to load backup history');
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggeringBackup(true);
    try {
      await backupsApi.trigger();
      toast.info('Backup initiated in the background. It will appear here once complete.');
      setTimeout(() => {
        fetchBackups();
      }, 3000);
    } catch (error) {
      console.error('Failed to trigger backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize backup task');
    } finally {
      setTriggeringBackup(false);
    }
  };

  const handleDownloadBackupFile = async (fileKey) => {
    try {
      const { data } = await backupsApi.getDownloadUrl(fileKey);
      if (data.download_url) {
        window.open(data.download_url, '_blank');
        toast.success('Backup file download started');
      } else {
        toast.error('Failed to retrieve download link');
      }
    } catch (error) {
      console.error('Failed to get download URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to download backup file');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to permanently delete this backup from Cloudflare R2? This action cannot be undone.')) {
      return;
    }
    try {
      await backupsApi.delete(backupId);
      toast.success('Backup deleted successfully');
      fetchBackups();
    } catch (error) {
      console.error('Failed to delete backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const handleTestR2Connection = async (e) => {
    e.preventDefault();
    if (!r2AccountId.trim() || !r2AccessKeyId.trim() || !r2SecretAccessKey.trim() || !r2BucketName.trim()) {
      toast.error('All R2 configuration fields are required for testing connectivity');
      return;
    }

    setTestingR2(true);
    try {
      await backupsApi.testConnection({
        account_id: r2AccountId.trim(),
        access_key_id: r2AccessKeyId.trim(),
        secret_access_key: r2SecretAccessKey.trim(),
        bucket_name: r2BucketName.trim(),
      });
      toast.success('Successfully connected to Cloudflare R2 bucket!');
    } catch (error) {
      console.error('R2 test connection failed:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed. Check your R2 settings.');
    } finally {
      setTestingR2(false);
    }
  };

  const handleSaveR2Config = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }

    setSavingR2(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        r2_config: {
          account_id: r2AccountId.trim(),
          access_key_id: r2AccessKeyId.trim(),
          secret_access_key: r2SecretAccessKey.trim(),
          bucket_name: r2BucketName.trim(),
          backup_frequency_hours: parseInt(r2BackupFrequency, 10) || 24,
        }
      });

      setTenantConfig(data);
      toast.success('R2 settings updated successfully!');
      
      const backupsRes = await backupsApi.list();
      setBackupsList(backupsRes.data.backups || []);
      setBackupsConfigured(backupsRes.data.is_configured || false);
    } catch (error) {
      console.error('Failed to save R2 settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save R2 settings');
    } finally {
      setSavingR2(false);
    }
  };

  const handleRestoreBackup = async (backupId) => {
    const confirm1 = window.confirm(
      "WARNING: Restoring a backup is a destructive action that will completely overwrite your existing database tables and media files with the state from this snapshot.\n\nAre you sure you want to proceed?"
    );
    if (!confirm1) return;

    const confirm2 = window.prompt(
      "Type 'RESTORE' in all caps to confirm and proceed with database and uploads restoration:"
    );
    if (confirm2 !== "RESTORE") {
      toast.error("Restoration aborted: confirmation word mismatch.");
      return;
    }

    setRestoringBackup(true);
    const toastId = toast.loading("Restoring application database and media archives from R2... Please wait, do not close this window.");
    try {
      await backupsApi.restore(backupId);
      toast.success("Application successfully restored to the selected snapshot!", { id: toastId });
      fetchBackups();
    } catch (error) {
      console.error("Backup restoration failed:", error);
      toast.error(error.response?.data?.detail || "Failed to restore backup snapshot. Database objects or credentials could be invalid.", { id: toastId });
    } finally {
      setRestoringBackup(false);
    }
  };

  const handleResetDb = async () => {
    const confirm1 = window.confirm(
      "CRITICAL WARNING: You are about to permanently drop all database tables and reset the application to default seed data.\n\nThis will ERASE all active tickets, technician job logs, customer records, and customized settings.\n\nAre you absolutely sure?"
    );
    if (!confirm1) return;

    const confirm2 = window.prompt(
      "Type 'RESET DATABASE' in all caps to confirm and proceed with database wipe:"
    );
    if (confirm2 !== "RESET DATABASE") {
      toast.error("Database reset aborted: confirmation mismatch.");
      return;
    }

    setResettingDb(true);
    const toastId = toast.loading("Wiping and re-seeding application database... Please wait.");
    try {
      await backupsApi.resetDb();
      toast.success("Database successfully reset and re-seeded to default state!", { id: toastId });
      fetchBackups();
    } catch (error) {
      console.error("Database reset failed:", error);
      toast.error(error.response?.data?.detail || "Failed to reset database.", { id: toastId });
    } finally {
      setResettingDb(false);
    }
  };

  return (
    <Card className="p-6 border shadow-sm rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-slate-100 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <Cloud size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-850">Application Backups</h3>
            <p className="text-slate-500 text-xs mt-0.5">
              Configure automated backup frequency, trigger manual database/uploads snapshots, and view storage metrics in Cloudflare R2.
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-4 self-end sm:self-auto">
          {/* Status & Storage Capacity Indicators */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                backupsConfigured ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'
              }`}></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {backupsConfigured ? 'R2 Configured' : 'R2 Missing'}
              </span>
            </div>

            {backupsConfigured && (
              <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Storage Consumed:
                </span>
                <span className="text-xs font-extrabold text-slate-700 bg-slate-50 border border-slate-200/60 px-2.5 py-1 rounded-md shadow-xs">
                  {formatBytes(totalCapacityBytes)}
                </span>
              </div>
            )}
          </div>

          {backupsSubTab === 'history' && (
            <div className="flex gap-2">
              <Button
                onClick={handleResetDb}
                disabled={resettingDb || triggeringBackup}
                variant="destructive"
                className="font-bold text-xs uppercase tracking-wider h-10 px-4"
                data-testid="reset-db-btn"
              >
                {resettingDb ? 'Resetting...' : 'Reset Database'}
              </Button>
              <Button
                onClick={handleTriggerBackup}
                disabled={triggeringBackup || !backupsConfigured}
                className="font-bold text-xs uppercase tracking-wider text-white h-10 px-4"
                style={{ backgroundColor: brandColorAccent }}
              >
                {triggeringBackup ? 'Triggering...' : 'Trigger Backup Now'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Sub-tab navigation */}
      <div className="flex border-b border-slate-100 mb-6 gap-6">
        <button
          onClick={() => setBackupsSubTab('history')}
          className={`pb-3 text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
            backupsSubTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-400 hover:text-slate-650'
          }`}
          style={backupsSubTab === 'history' ? { borderColor: brandColorAccent, color: brandColorAccent } : {}}
        >
          Backup History
        </button>
        <button
          onClick={() => setBackupsSubTab('settings')}
          className={`pb-3 text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
            backupsSubTab === 'settings'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-400 hover:text-slate-650'
          }`}
          style={backupsSubTab === 'settings' ? { borderColor: brandColorAccent, color: brandColorAccent } : {}}
        >
          R2 Settings
        </button>
      </div>

      {backupsSubTab === 'history' ? (
        <>
          {loadingBackups ? (
            <div className="py-12 flex justify-center items-center">
              <ArrowClockwise className="animate-spin text-slate-400" size={32} />
            </div>
          ) : backupsList.length > 0 ? (
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                      <th className="py-3 px-4">Backup Date</th>
                      <th className="py-3 px-4">Database Dump</th>
                      <th className="py-3 px-4">Uploads Archive</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {[...backupsList].reverse().map((backup) => {
                      const isSuccess = backup.status === 'success';

                      return (
                        <tr key={backup.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-slate-900">
                              {new Date(backup.timestamp).toLocaleString()}
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                              {backup.id}
                            </div>
                          </td>
                          <td className="py-3.5 px-4 font-semibold text-slate-600">
                            {isSuccess ? formatBytes(backup.db_file_size) : '—'}
                          </td>
                          <td className="py-3.5 px-4 font-semibold text-slate-600">
                            {isSuccess ? formatBytes(backup.uploads_file_size) : '—'}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                              isSuccess
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                            }`}>
                              {backup.status}
                            </span>
                            {backup.error && (
                              <div className="text-[10px] text-red-500 font-medium mt-1 max-w-[200px] truncate" title={backup.error}>
                                {backup.error}
                              </div>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-right space-x-1 whitespace-nowrap">
                            {isSuccess && (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={restoringBackup}
                                  onClick={() => handleRestoreBackup(backup.id)}
                                  className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 px-2"
                                >
                                  Restore
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadBackupFile(backup.db_file_key)}
                                  className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-primary hover:bg-slate-100 h-8 px-2"
                                >
                                  DB Dump
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadBackupFile(backup.uploads_file_key)}
                                  className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-primary hover:bg-slate-100 h-8 px-2"
                                >
                                  Uploads
                                </Button>
                              </>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={restoringBackup}
                              onClick={() => handleDeleteBackup(backup.id)}
                              className="text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 px-2"
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-12 border border-dashed rounded-lg text-center space-y-2">
              <Cloud className="mx-auto text-slate-300" size={40} />
              <h3 className="font-bold text-slate-700">No Backups Found</h3>
              {backupsConfigured ? (
                <p className="text-xs text-slate-400">Trigger your first manual backup using the button above.</p>
              ) : (
                <p className="text-xs text-red-400 font-medium">Please configure Cloudflare R2 credentials to utilize backups.</p>
              )}
            </div>
          )}
        </>
      ) : (
        <form onSubmit={handleSaveR2Config} className="space-y-6 max-w-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="r2-account-id" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Account ID
              </label>
              <input
                id="r2-account-id"
                type="text"
                value={r2AccountId}
                onChange={(e) => setR2AccountId(e.target.value)}
                placeholder="e.g. 5d57b2ffb38a7c293792cb00b73138b1"
                className="w-full text-xs font-semibold bg-slate-50/50 border border-slate-200 rounded px-3 py-2.5 outline-none focus:border-slate-450 focus:bg-white transition-all placeholder:text-slate-350 text-slate-700"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="r2-bucket-name" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Bucket Name
              </label>
              <input
                id="r2-bucket-name"
                type="text"
                value={r2BucketName}
                onChange={(e) => setR2BucketName(e.target.value)}
                placeholder="e.g. my-resolv-backups"
                className="w-full text-xs font-semibold bg-slate-50/50 border border-slate-200 rounded px-3 py-2.5 outline-none focus:border-slate-450 focus:bg-white transition-all placeholder:text-slate-350 text-slate-700"
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label htmlFor="r2-access-key-id" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Access Key ID
              </label>
              <input
                id="r2-access-key-id"
                type="text"
                value={r2AccessKeyId}
                onChange={(e) => setR2AccessKeyId(e.target.value)}
                placeholder="e.g. c847d0deae75a34e062eb1473bd4ee7c"
                className="w-full text-xs font-semibold bg-slate-50/50 border border-slate-200 rounded px-3 py-2.5 outline-none focus:border-slate-450 focus:bg-white transition-all placeholder:text-slate-350 text-slate-700"
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2 relative">
              <label htmlFor="r2-secret-access-key" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Secret Access Key
              </label>
              <div className="relative">
                <input
                  id="r2-secret-access-key"
                  type={showR2Secret ? 'text' : 'password'}
                  value={r2SecretAccessKey}
                  onChange={(e) => setR2SecretAccessKey(e.target.value)}
                  placeholder={r2SecretConfigured ? 'Already set — leave blank to keep it' : '••••••••••••••••••••••••••••••••'}
                  className="w-full text-xs font-semibold bg-slate-50/50 border border-slate-200 pl-3 pr-10 py-2.5 outline-none focus:border-slate-450 focus:bg-white transition-all placeholder:text-slate-350 text-slate-700 font-mono"
                  required={!r2SecretConfigured}
                />
                <button
                  type="button"
                  onClick={() => setShowR2Secret(!showR2Secret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-650"
                >
                  {showR2Secret ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label htmlFor="r2-backup-frequency" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Backup Frequency (Hours)
              </label>
              <input
                id="r2-backup-frequency"
                type="number"
                min="1"
                max="168"
                value={r2BackupFrequency}
                onChange={(e) => setR2BackupFrequency(e.target.value)}
                placeholder="e.g. 24"
                className="w-full text-xs font-semibold bg-slate-50/50 border border-slate-200 rounded px-3 py-2.5 outline-none focus:border-slate-450 focus:bg-white transition-all placeholder:text-slate-350 text-slate-700"
                required
              />
              <p className="text-[10px] text-slate-400 font-medium">Specify how frequently (in hours) the automatic scheduler should back up your database and uploads folder.</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              disabled={testingR2 || savingR2}
              onClick={handleTestR2Connection}
              className="font-bold text-xs uppercase tracking-wider border border-slate-200 hover:bg-slate-50 text-slate-700 h-10 px-4"
            >
              {testingR2 ? 'Testing Connection...' : 'Test Connection'}
            </Button>
            <Button
              type="submit"
              disabled={savingR2 || testingR2}
              className="font-bold text-xs uppercase tracking-wider text-white h-10 px-4"
              style={{ backgroundColor: brandColorAccent }}
            >
              {savingR2 ? 'Saving Settings...' : 'Save R2 Credentials'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
};
